import { Button } from '@/components/ui';
import { dealStageLabel, formatCrmDate, formatDealAmount, type CrmAccount } from '@/lib/crm-account';

const shown = (count: number, total: number) => total > count ? ` · ${count} sur ${total}` : '';

/** HubSpot facts of a Domino account. Absence of loaded links is never shown as "no deal". */
export const CrmAccountFacts = ({ account, error, onRetry, onSync, syncing = false, showCompany = true, showContacts = true }: {
  account: CrmAccount | null;
  /** The account page's header already says who the company is (26/10). */
  showCompany?: boolean;
  /** The account page shows its contacts in « Personnes » (26/10). */
  showContacts?: boolean;
  error: string | null;
  onRetry: () => void;
  /** Explicit re-read of the CRM, offered while this account was never read. */
  onSync?: () => void;
  syncing?: boolean;
}) => {
  if (error) {
    return <section className="border-b border-domino-cardBorder py-5 text-sm text-red-700" role="alert">
      {error} <Button className="ml-2" variant="secondary" size="sm" onClick={onRetry}>Réessayer</Button>
    </section>;
  }
  if (!account) return null;
  if (account.status === 'not_synced') {
    return <section className="border-b border-domino-cardBorder py-5 text-sm text-domino-inkSoft">
      Ce compte n’a pas encore été lu depuis HubSpot.
      {onSync && <Button className="ml-2" variant="secondary" size="sm" onClick={onSync} disabled={syncing}>
        {syncing ? 'Lecture…' : 'Lire depuis HubSpot'}
      </Button>}
    </section>;
  }
  const { company } = account;
  const place = [company.city, company.country].filter(Boolean).join(', ');
  const summary = [company.domain, company.industry, place || null].filter(Boolean).join(' · ');
  const linksLoaded = account.associationsStatus === 'complete' || account.associationsStatus === 'partial';
  const observed = formatCrmDate(account.observedAt);

  return <section aria-labelledby="crm-account-title" className="border-b border-domino-cardBorder py-5">
    <h2 id="crm-account-title" className="text-sm font-semibold text-domino-ink">HubSpot</h2>
    {showCompany && summary && <p className="mt-1 text-sm text-domino-inkSoft">{summary}</p>}
    {!linksLoaded ? (
      <p className="mt-3 text-sm text-domino-inkSoft">Opportunités et contacts pas encore chargés depuis HubSpot.</p>
    ) : (
      <div className={showContacts ? 'mt-4 grid gap-6 md:grid-cols-2' : 'mt-4'}>
        <div>
          <h3 className="text-sm font-medium text-domino-inkSoft">
            Opportunités{shown(account.deals.length, account.dealsTotal)}
          </h3>
          {account.deals.length === 0 ? (
            <p className="mt-2 text-sm text-domino-inkSoft">Aucune opportunité dans HubSpot.</p>
          ) : <ul className="mt-2 space-y-2">
            {account.deals.map((deal) => {
              const details = [dealStageLabel(deal.stage), formatDealAmount(deal.amount),
                deal.closeDate && formatCrmDate(deal.closeDate) ? `clôture ${formatCrmDate(deal.closeDate)}` : null].filter(Boolean);
              return <li key={deal.id} className="text-sm">
                <span className="font-medium text-domino-ink">{deal.name ?? 'Opportunité sans nom'}</span>
                {details.length > 0 && <span className="block text-domino-inkSoft">{details.join(' · ')}</span>}
              </li>;
            })}
          </ul>}
        </div>
        {showContacts && <div>
          <h3 className="text-sm font-medium text-domino-inkSoft">
            Contacts{shown(account.contacts.length, account.contactsTotal)}
          </h3>
          {account.contacts.length === 0 ? (
            <p className="mt-2 text-sm text-domino-inkSoft">Aucun contact associé dans HubSpot.</p>
          ) : <ul className="mt-2 space-y-2">
            {account.contacts.map((contact) => <li key={contact.id} className="text-sm">
              <span className="font-medium text-domino-ink">{contact.name ?? contact.email ?? 'Contact sans nom'}</span>
              {(contact.jobTitle || (contact.name && contact.email)) && <span className="block text-domino-inkSoft">
                {[contact.jobTitle, contact.name ? contact.email : null].filter(Boolean).join(' · ')}
              </span>}
            </li>)}
          </ul>}
        </div>}
      </div>
    )}
    {(account.notesStatus === 'complete' || account.notesStatus === 'partial') && account.notes.length > 0 && (
      <div className="mt-5">
        <h3 className="text-sm font-medium text-domino-inkSoft">
          Dernières notes{shown(Math.min(account.notes.length, 3), account.notesTotal)}
        </h3>
        <ul className="mt-2 space-y-3">
          {account.notes.slice(0, 3).map((note) => <li key={note.id} className="text-sm">
            {formatCrmDate(note.timestamp) && <span className="block text-xs text-domino-inkSoft">{formatCrmDate(note.timestamp)}</span>}
            <p className="whitespace-pre-line text-domino-ink">{note.body}</p>
          </li>)}
        </ul>
      </div>
    )}
    {account.associationsStatus === 'partial' && (
      <p className="mt-3 text-xs text-domino-inkSoft">Certaines associations HubSpot n’ont pas toutes été lues.</p>
    )}
    {observed && <p className="mt-3 text-xs text-domino-inkSoft">Lu dans HubSpot le {observed}</p>}
  </section>;
};
